import { compare as compareSemver } from 'semver'
import { v4 } from 'uuid'

import { AbortError } from '@app/errors/AbortError'

import { captureException } from '@app/utils/errorReport/errorReport'
import { createLogger } from '@app/utils/loggerCreator'
import type { ApplicationAgentData } from '@app/utils/parseApplicationAgent'
import { sleep } from '@app/utils/sleep'

import type { NavigationAction } from '@app/packages/routing/types'

import type { Store } from '@app/store/store'

export type ApplicationMessageEventProvider = 'yandex' | 'google' | 'amplitude' | 'sentry' | 'trackings'

export type ApplicationMessage =
  | {
      /** since 3.40 */
      type: 'suspend_resolved'
    }
  | { type: 'registration_completed' }
  | {
      /**
       * pre 3.40, superceeded with `open_support_chat`
       * before we used jivo chat, but since we discarded it old apps just open `mailto:help@kidsout.ru`
       */
      type: 'open_sitter_support_chat'
    }
  | {
      /** since 3.40 */
      type: 'open_support_chat'
    }
  | { type: 'payment_card_change' }
  | { type: 'mount_stack_change'; stack_length: number }
  | { type: 'tos_accepted' }
  | { type: 'how_it_works_search_pressed' }
  | { type: 'how_it_works_support_pressed' }
  | {
      /** since 3.40, passes event that app should reply with callback, see `application_action_callback`  */
      type: 'application_action'
      action: string
      id: string
      payload: any
    }
  | {
      type: 'amplitude_event'
      event: string
      event_properties?: { [key: string]: any }
      providers?: ApplicationMessageEventProvider[]
    }
  | { type: 'copy_to_clipboard'; text: string }

export type ApplicationIncomingMessage =
  | { type: 'pop_modal' }
  | {
      /** since 3.40, receives if application action event callback, see `application_action` */
      type: 'application_action_callback'
      action: string
      id: string
      error?: string
      result: any
    }
  | {
      /**
       * The app pushes a fresh access token (e.g. ahead of expiry) so the webview
       * can keep authenticating without its own refresh session. The token is a JWT.
       */
      type: 'token_refresh'
      access_token: string
    }

type ApplicationActionTemplate<T extends string, P, CB> = {
  type: T
  payload: P
  callback: CB
}

export type ApplicationAction =
  | ApplicationActionTemplate<
      'navigation',
      {
        pathname: string
        search?: string
        hash?: string
        action: NavigationAction
      },
      { handled: boolean }
    >
  /**
   * Ask the app for a token that is valid right now — since 3.46.0, `appProtocol >= 4`. The app
   * runs its own `ensureAccessToken` first, which is the point: the page asks precisely when its
   * own copy has aged out and it has no refresh material of its own.
   *
   * `access_token: null` is an answer, not a failure — nobody is signed in.
   */
  | ApplicationActionTemplate<'session_token', {}, { access_token: string | null }>

export type ApplicationActionName = ApplicationAction['type']
export type ApplicationActionPayload<T extends ApplicationActionName> = Extract<ApplicationAction, { type: T }>['payload']
export type ApplicationActionCallback<T extends ApplicationActionName> = Extract<ApplicationAction, { type: T }>['callback']

export class ApplicationBridge {
  /** Can be null for old apps */
  agentData: ApplicationAgentData | null
  store: Store

  constructor(agentData: ApplicationAgentData | null, store: Store) {
    this.agentData = agentData
    this.store = store
  }

  private listeners: ((event: ApplicationIncomingMessage) => unknown)[] = []

  registerIncomingHandler() {
    ;(window as any).ReactNativeWebViewReceiveMessage = (event: ApplicationIncomingMessage) => {
      this.listeners.forEach(l => l(event))
    }
  }

  sendMessage(message: ApplicationMessage) {
    try {
      this.logger.log('Sending message', message)
      const smessage = JSON.stringify({ 'ru.kidsout.app.payload': 1, ...message })
      ;(window as any).ReactNativeWebView?.postMessage?.(smessage)
    } catch (e) {
      captureException(e)
    }
  }

  /** @returns unsubscribe function */
  addMessageListener(listener: (event: ApplicationIncomingMessage) => unknown) {
    this.listeners.push(listener)
    return () => {
      const index = this.listeners.indexOf(listener)
      if (index >= 0) {
        this.listeners.splice(index, 1)
      }
    }
  }

  matchVersion(version: string) {
    if (!this.agentData) return -1
    return compareSemver(this.agentData.version, version)
  }

  canSendAction() {
    if (!this.agentData) return false
    return compareSemver(this.agentData.version, '3.40.0') >= 0
  }

  /** Narrowed by the action name, so each action's payload and callback are its own. */
  sendAction<T extends ApplicationActionName>(action: T, payload: ApplicationActionPayload<T>, timeout = 5000): Promise<ApplicationActionCallback<T>> {
    const id = v4()

    const ab = new AbortController()
    const p = Promise.withResolvers<ApplicationAction['callback']>()
    this.sendMessage({ type: 'application_action', action, id, payload })
    const unsub = this.addMessageListener(event => {
      if (event.type === 'application_action_callback' && event.action === action && event.id === id) {
        if (event.error) {
          p.reject(new ApplicationActionError(event.error))
        } else {
          p.resolve(event.result)
        }
        ab.abort()
        unsub()
      }
    })

    sleep(timeout, ab.signal)
      .then(() => {
        if (!ab.signal.aborted) {
          p.reject(new AbortError(`Application action timeout for ${action}:${id}`))
          ab.abort()
          unsub()
        }
      })
      .catch(() => {})

    // The reply is matched on `action` and `id` above, so it is this action's own callback — a
    // correlation drawn at runtime that the message union cannot express.
    return p.promise as Promise<ApplicationActionCallback<T>>
  }

  private logger = createLogger('ApplicationBridge', 'green')
}

export class ApplicationActionError extends Error {}
